// BusinessesListPage — US-104 + US-105 entry.
//
// Lists every non-archived business via `useBusinesses()` + the DS
// `BusinessCard` composite (Wave-D). Click → `/businesses/<id>` editor.
// Empty state mirrors the campaigns/leads empty-state pattern.

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { useBusinesses, type BusinessListItem } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  BusinessCard,
  BusinessCardSkeleton,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  PageHeader,
  type BusinessSummary,
} from '@barel/ui';

import { CreateBusinessForm } from './CreateBusinessForm';
import { useLinkedinAccountsList } from './lib/useLinkedinAccountsList';

function toSummary(
  business: BusinessListItem,
  accountName: string | undefined,
  campaignsLabel: (count: number) => string,
  valuePropLabel: string,
  editLabel: string,
): { summary: BusinessSummary; copy: Parameters<typeof BusinessCard>[0]['copy'] } {
  return {
    summary: {
      id: business.id,
      name: business.name,
      account: accountName ?? '—',
      offer: business.description || '',
      valueProp: business.valueProps[0] ?? '',
      campaignCount: business.campaignCount,
    },
    copy: {
      campaignsChip: (count) => campaignsLabel(count),
      valuePropLabel,
      editLabel,
    },
  };
}

export function BusinessesListPage() {
  const t = useT('businesses');
  const navigate = useNavigate();
  const businesses = useBusinesses();
  const accounts = useLinkedinAccountsList();
  const [createOpen, setCreateOpen] = useState(false);

  const accountById = useMemo(
    () => new Map(accounts.data.map((account) => [account.id, account])),
    [accounts.data],
  );

  const editLabel = t('list.editLabel');
  const valuePropLabel = t('list.valuePropLabel');
  const campaignsLabel = (count: number) => t('list.campaignsChip', { count });

  return (
    <section className="flex flex-col gap-6" data-testid="businesses-list">
      <PageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        actions={
          <Button
            variant="primary"
            size="md"
            onClick={() => setCreateOpen(true)}
            data-testid="businesses-list-new"
          >
            {t('list.newButton')}
          </Button>
        }
      />

      {businesses.isLoading ? (
        <div className="flex flex-col gap-3" data-testid="businesses-list-loading">
          <BusinessCardSkeleton aria-label={t('list.loadingLabel')} />
          <BusinessCardSkeleton aria-label={t('list.loadingLabel')} />
        </div>
      ) : null}

      {businesses.isError ? (
        <Card padding="lg" radius="2xl" data-testid="businesses-list-error">
          <Banner tone="error" title={t('list.errorTitle')}>
            {t('list.errorBody')}
          </Banner>
        </Card>
      ) : null}

      {!businesses.isLoading &&
      !businesses.isError &&
      (businesses.data?.businesses.length ?? 0) === 0 ? (
        <Card padding="lg" radius="2xl" data-testid="businesses-list-empty">
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <strong className="text-primary">{t('list.emptyTitle')}</strong>
            <p className="text-sm text-secondary">{t('list.emptySubtitle')}</p>
            <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
              {t('list.newButton')}
            </Button>
          </div>
        </Card>
      ) : null}

      {!businesses.isLoading && !businesses.isError && businesses.data?.businesses.length ? (
        <div className="flex flex-col gap-3" data-testid="businesses-list-rows">
          {businesses.data.businesses.map((business) => {
            const account = business.linkedinAccountId
              ? accountById.get(business.linkedinAccountId)
              : undefined;
            const { summary, copy } = toSummary(
              business,
              account?.name,
              campaignsLabel,
              valuePropLabel,
              editLabel,
            );
            return (
              <BusinessCard
                key={business.id}
                business={summary}
                copy={copy}
                onEdit={(id) => {
                  void navigate({ to: '/businesses/$id', params: { id } });
                }}
              />
            );
          })}
        </div>
      ) : null}

      <Dialog open={createOpen} onOpenChange={(next) => (!next ? setCreateOpen(false) : undefined)}>
        <DialogContent size="md" data-testid="businesses-create-modal">
          <DialogHeader
            title={t('create.title')}
            description={t('create.subtitle')}
            onClose={() => setCreateOpen(false)}
          />
          <DialogBody>
            <CreateBusinessForm
              onCreated={(id) => {
                setCreateOpen(false);
                void navigate({ to: '/businesses/$id', params: { id } });
              }}
              onCancel={() => setCreateOpen(false)}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </section>
  );
}
